import React from "react";
import { CheckCircle2, Clock, XCircle, ClipboardList } from "lucide-react";

// Mandatory Implementation Register per Executive Directive.
// Every future Emergent recommendation MUST consult this register first.
const STATUS_ICON = {
  complete: <CheckCircle2 size={14} className="text-[#6BB88A]"/>,
  partial: <Clock size={14} className="text-[#C99A2E]"/>,
  missing: <XCircle size={14} className="text-[#E67E7E]"/>,
};

const REGISTER = [
  { id: 1, name: "Draw-On-Map Editing Engine", status: "partial", version: "0.8",
    files: ["src/components/GISMap.jsx", "src/components/TwinMap.jsx", "src/pages/SpatialValidation.jsx"],
    dbs: ["parcels", "mineral_rights"],
    apis: ["POST /api/spatial/validate", "POST /api/mining/rights"],
    routes: ["/app/spatial", "/app/twin"],
    limitations: "Real Shapely topology, protected-area overlap and area calc live. Interactive vertex-edit / split / merge / snapping UI pending. Coordinate import UI (SHP/DXF/GPS) pending.",
    regressed: "no", owner: "Surveyor-General" },
  { id: 2, name: "Intelligent Subdivision Workflow", status: "partial", version: "0.5",
    files: ["src/pages/Applications.jsx", "backend/enterprise_routes.py"],
    dbs: ["applications", "parcels"],
    apis: ["POST /api/applications (subdivision type)"],
    routes: ["/app/applications"],
    limitations: "Subdivision is a supported application_type; auto-parcel-creation + zoning validation + frontage/lot-size checks pending.",
    regressed: "no", owner: "Land Registrar" },
  { id: 3, name: "Enterprise Payment & Revenue", status: "partial", version: "0.7",
    files: ["src/pages/Revenue.jsx", "backend/enterprise_routes.py"],
    dbs: ["revenue_ledger"], apis: ["/api/revenue/*"],
    routes: ["/app/revenue"],
    limitations: "Fee schedule + calculator + ledger + donut breakdown live. Live gateway (RTGS/Mobile Money/Card/Treasury) pending.",
    regressed: "no", owner: "Treasury liaison" },
  { id: 4, name: "Trusted Digital Certificates (PKI)", status: "partial", version: "0.6",
    files: ["backend/phase2_routes.py (_sign_payload)"],
    dbs: ["—"], apis: ["/api/signatures/verify/{ref}", "/api/certificates/{ref}"],
    routes: ["/verify/:ref"],
    limitations: "SHA-256 signature + 3-tier trust chain + TSA + revocation model live. Real X.509 CA + OCSP + PDF-embedded signatures + LTV require live PKI infrastructure.",
    regressed: "no", owner: "Chief Executive" },
  { id: 5, name: "3D Digital Twin", status: "partial", version: "0.4",
    files: ["src/components/TwinMap.jsx"],
    dbs: ["—"], apis: ["/api/twin/layers"],
    routes: ["/app/twin"],
    limitations: "4 basemaps (Cadastral/Satellite/Terrain/Hybrid) + 10 layer catalogue live. True 3D (Cesium/DEM/LiDAR/building heights) pending — requires Cesium ion assets.",
    regressed: "no", owner: "Surveyor-General" },
  { id: 6, name: "Historical Time Slider", status: "partial", version: "0.5",
    files: ["src/pages/HistoricalTimeline.jsx"],
    dbs: ["parcel_history", "parcels.history"],
    apis: ["GET /api/land/parcels/{id} (existing endpoint reused)"],
    routes: ["/app/history"],
    limitations: "Year slider + event timeline live consuming existing parcel_history. Compare-two-dates, restore-historical-state, AI narrative and boundary-evolution playback pending.",
    regressed: "no", owner: "Land Registrar" },
  { id: 7, name: "Ministerial Executive View", status: "complete", version: "1.0",
    files: ["src/pages/Dashboard.jsx", "src/pages/Performance.jsx", "src/pages/Briefings.jsx"],
    dbs: ["all"], apis: ["/api/executive/overview", "/api/performance/{role}", "/api/briefings/*"],
    routes: ["/app", "/app/performance", "/app/briefings"],
    limitations: "Situation Room + Minister/PermSec/SG/MC/LR dashboards + 16 briefing scopes live.",
    regressed: "no", owner: "Executive Director" },
  { id: 8, name: "AI Spatial Intelligence", status: "partial", version: "0.7",
    files: ["src/pages/Intelligence.jsx", "src/pages/IntegrityCentre.jsx", "backend/server.py"],
    dbs: ["—"], apis: ["/api/ai/brief", "/api/ai/chat", "/api/integrity/anomalies", "/api/spatial/validate"],
    routes: ["/app/intelligence", "/app/integrity"],
    limitations: "GPT executive briefs + anomaly analytics + spatial validation live. Encroachment / illegal-development / flood-prone AI classifiers pending.",
    regressed: "no", owner: "Executive Director" },
  { id: 9, name: "Field Surveyor Mobile Experience", status: "partial", version: "0.3",
    files: ["src/pages/Inspections.jsx"],
    dbs: ["inspections"], apis: ["/api/inspections"], routes: ["/app/inspections"],
    limitations: "Inspection register + assignment + scheduling live. Offline PWA capture + RTK / photograph geotagging pending.",
    regressed: "no", owner: "Surveyor-General" },
  { id: 10, name: "Demonstration Environment", status: "complete", version: "1.0",
    files: ["backend/seed_data.py"],
    dbs: ["600 parcels · 420 mineral rights · 140 apps · 90 surveys · 45 inspections · 25 disputes · 220 revenue · 18 notices · 5 knowledge"],
    apis: ["—"], routes: ["all"],
    limitations: "National-scale realistic demo across 10 provinces, all right/tenure types, all commodities.",
    regressed: "no", owner: "Data seed" },
  { id: 11, name: "Progressive Data Migration™", status: "partial", version: "0.6",
    files: ["src/pages/DigitisationCentre.jsx", "backend/enterprise_routes.py"],
    dbs: ["digitisation_batches"], apis: ["/api/digitisation/*"],
    routes: ["/app/digitisation"],
    limitations: "Batch-per-source model already supports staged migration (parcels → deeds → surveys → planning → environment → valuation as separate batches). Formal 7-stage wizard UI pending.",
    regressed: "no", owner: "Records Officer" },
  { id: 12, name: "AI Data Quality Engine", status: "partial", version: "0.7",
    files: ["backend/enterprise_routes.py", "backend/phase2_routes.py"],
    dbs: ["—"], apis: ["/api/spatial/validate", "/api/integrity/anomalies", "/api/digitisation/classify"],
    routes: ["/app/spatial", "/app/integrity"],
    limitations: "Duplicate/overlap/topology/self-intersection/protected-area detection live. Missing-owner / missing-CRS heuristics pending.",
    regressed: "no", owner: "Records Officer" },
  { id: 13, name: "National Land Data Migration & Acceleration Platform", status: "complete", version: "1.0",
    files: ["backend/migration_routes.py", "src/pages/MigrationCentre.jsx", "src/pages/MigrationSituationRoom.jsx"],
    dbs: ["migration_projects", "migration_batches", "migration_records", "migration_stage_events", "migration_audit"],
    apis: ["/api/migration/projects", "/api/migration/batches", "/api/migration/batches/{id}/validate|approve|publish|rollback",
           "/api/migration/records/{id}/advance", "/api/migration/extraction", "/api/migration/dashboard",
           "/api/migration/quality", "/api/migration/productivity", "/api/migration/forecast",
           "/api/migration/situation", "/api/migration/audit", "/api/migration/reference"],
    routes: ["/app/migration", "/app/migration/situation"],
    limitations: "11-module national migration platform: bulk import (11 formats), 9-stage progressive workflow, AI validation, survey extraction, data quality intelligence, productivity, forecasting, executive situation room, immutable audit. Real GDAL/OGR full-format parsing binds at deployment.",
    regressed: "no", owner: "National Migration Programme" },
  { id: 14, name: "Enterprise Integration Hub", status: "complete", version: "1.0",
    files: ["src/pages/IntegrationHub.jsx", "backend/phase3_routes.py"],
    dbs: ["—"], apis: ["/api/integration/status"], routes: ["/app/integration"],
    limitations: "10 ministry integrations catalogued with status/latency/last-sync. Outbound webhook execution pending.",
    regressed: "no", owner: "Integration lead" },
  { id: 15, name: "Master Land Record", status: "complete", version: "1.0",
    files: ["src/pages/LandCadastre.jsx", "backend/server.py"],
    dbs: ["parcels", "parcel_history"], apis: ["/api/land/*"],
    routes: ["/app/land"],
    limitations: "Parcel model already carries tenure, rights_holder, restrictions, title_registered, history, zoning, land_use, servitudes, encumbrances hooks.",
    regressed: "no", owner: "Land Registrar" },
  { id: 16, name: "Universal GIS Engine", status: "complete", version: "1.0",
    files: ["src/components/GISMap.jsx", "src/components/TwinMap.jsx"],
    dbs: ["—"], apis: ["/api/twin/layers"], routes: ["/app/twin", "/app/land", "/app/mining"],
    limitations: "Leaflet + 4 basemaps + OSM/Esri/OpenTopoMap. Cesium 3D pending.",
    regressed: "no", owner: "Surveyor-General" },
  { id: 17, name: "Document Intelligence (OCR + AI)", status: "complete", version: "1.0",
    files: ["src/pages/DigitisationCentre.jsx", "backend/enterprise_routes.py"],
    dbs: ["digitisation_batches"], apis: ["/api/digitisation/classify"],
    routes: ["/app/digitisation"],
    limitations: "AI classification via Emergent LLM live. Multi-page raw-scan pipeline requires external OCR service.",
    regressed: "no", owner: "Records Officer" },
  { id: 18, name: "Digital Adoption Platform", status: "missing", version: "0.0",
    files: [], dbs: ["—"], apis: ["—"], routes: ["—"],
    limitations: "No in-app walkthroughs, tooltips or role-specific help engine. Genuine gap.",
    regressed: "no", owner: "Product / UX" },
  { id: 19, name: "Continuous Learning & Competency", status: "missing", version: "0.0",
    files: [], dbs: ["—"], apis: ["—"], routes: ["—"],
    limitations: "No competency / training / certification tracking. Genuine gap.",
    regressed: "no", owner: "Product / HR" },
  { id: 20, name: "Citizen Self-Service Portal", status: "complete", version: "1.0",
    files: ["src/pages/ApplicantPortal.jsx", "src/pages/PublicPortal.jsx", "src/pages/PublicSearch.jsx"],
    dbs: ["application_drafts", "application_documents", "applications"],
    apis: ["/api/portal/my/*", "/api/public/*"],
    routes: ["/app/portal", "/portal", "/portal/search", "/portal/map", "/verify/:ref"],
    limitations: "Drafts, fee calc, upload, submit, status track live. Appointment booking pending.",
    regressed: "no", owner: "Applicant portal team" },
  { id: 21, name: "Performance Engineering", status: "partial", version: "0.6",
    files: ["backend/server.py (indexes)", "src/index.js (React Query)"],
    dbs: ["all — partial-filter unique indexes"],
    apis: ["—"], routes: ["—"],
    limitations: "Async Motor + React Query caching + partial-filter unique indexes. Redis + job queue + spatial index tiles pending.",
    regressed: "no", owner: "Platform team" },
  { id: 22, name: "Reporting Platform", status: "complete", version: "1.0",
    files: ["src/pages/Reports.jsx", "backend/enterprise_routes.py"],
    dbs: ["all"], apis: ["/api/reports/*"], routes: ["/app/reports"],
    limitations: "PDF + Excel with sovereign masthead + digital signature block. Word/PPT deferred.",
    regressed: "no", owner: "Reporting team" },
  { id: 23, name: "Mining Cadastre Migration Acceleration Layer", status: "complete", version: "1.0",
    files: ["backend/mining_migration_routes.py",
            "src/pages/MiningMigrationCentre.jsx", "src/pages/MiningMigrationCommand.jsx",
            "src/pages/MiningSurveyorPortal.jsx", "src/pages/MiningClaimantPortal.jsx"],
    dbs: ["mining_migration_submissions", "mining_migration_documents", "mining_migration_coordinates",
          "mining_migration_geometries", "mining_migration_validation_results", "mining_migration_conflicts",
          "mining_migration_exceptions", "mining_migration_reviews", "mining_migration_approvals",
          "mining_migration_commit_transactions", "mining_migration_import_batches",
          "mining_migration_field_mappings", "mining_migration_data_quality_scores",
          "mining_migration_go_live", "mining_migration_surveyor_submissions",
          "mining_migration_claimant_submissions", "mining_migration_audit_events"],
    apis: ["/api/cadastre360/migration/reference", "…/submissions", "…/extraction", "…/validate",
           "…/crs", "…/imports", "…/mappings", "…/conflicts", "…/exceptions",
           "…/claimant/submissions", "…/surveyor/submissions", "…/reviews",
           "…/commit/{id}", "…/rollback/{id}", "…/progress", "…/data-quality",
           "…/go-live", "…/audit"],
    routes: ["/app/mining-migration", "/app/mining-migration/command",
             "/app/mining-migration/surveyors", "/app/mining-migration/claimants"],
    limitations: "15-capability acceleration layer: legacy intake, AI coordinate extraction, validation engine, CRS management, bulk ETL (10 formats), AI field mapping, duplicate/conflict detection, claimant + surveyor portals, officer workbench, exception queues, progress command centre, data-quality dashboard, progressive go-live, controlled register commit, immutable audit. Feature flag: cadastre360_mining_migration_accelerator. Real GDAL/OGR full-format parsing binds at deployment.",
    regressed: "no", owner: "Mining Cadastre Migration Programme" },
  { id: 24, name: "Enterprise Land Intelligence & National Spatial Analytics", status: "complete", version: "1.0",
    files: ["backend/land_intelligence_routes.py", "src/pages/EnterpriseIntelligence.jsx"],
    dbs: ["intel_investment_zones", "intel_infrastructure", "intel_heat_cells",
          "intel_scenarios", "intel_briefings", "intel_forecasts",
          "intel_spatial_ai_findings"],
    apis: ["/api/cadastre360/intelligence/reference", "/api/cadastre360/intelligence/national-overview",
           "/api/cadastre360/intelligence/provincial", "/api/cadastre360/intelligence/investment",
           "/api/cadastre360/intelligence/land-utilisation", "/api/cadastre360/intelligence/heatmaps",
           "/api/cadastre360/intelligence/heatmaps/{type}", "/api/cadastre360/intelligence/forecasts",
           "/api/cadastre360/intelligence/scenarios", "/api/cadastre360/intelligence/briefings",
           "/api/cadastre360/intelligence/spatial-ai", "/api/cadastre360/intelligence/twin-bridge"],
    routes: ["/app/enterprise-intelligence"],
    limitations: "9-tab executive intelligence hub: National Overview, Provincial comparison, Investment Intelligence, National Land Utilisation, Executive Heat Maps (9 types + Leaflet map), Predictive Analytics (9 forecasts × 12 months), Scenario Modelling (independent simulation), Executive Briefings (5 cadences, AI-generated), Spatial AI (8 finding types). Read-only aggregations — no dashboard modifies production records.",
    regressed: "no", owner: "Executive Intelligence Programme" },
  { id: 25, name: "Administration Console — Identity, Audit & Federation Framework", status: "complete", version: "1.0",
    files: ["backend/admin_console_routes.py", "src/pages/AdminConsole.jsx"],
    dbs: ["identity_admin_audit", "identity_providers"],
    apis: ["/api/cadastre360/admin/users", "…/users/{id}/reset-password", "…/users/{id}/unlock",
           "…/users/{id}/force-change", "…/users/{id}/activation", "…/users/{id}/login-history",
           "…/login-failures", "…/audit", "…/audit/export.csv",
           "…/federation/providers", "…/federation/providers/{key}", "…/health"],
    routes: ["/app/admin-console"],
    limitations: "4-tab admin workspace: Identity Administration (reset/unlock/force-change/activate/deactivate over existing bcrypt+JWT), unified Audit & Compliance Centre with CSV export across 3 audit collections, modular Enterprise Identity Federation Framework registering 6 providers (Local enabled by default; Entra ID · Google Workspace · SAML 2.0 · OIDC · LDAP disabled by default), Deployment Health probe. Providers other than Local require an administrator to enable and load their secrets via the approved integration playbook.",
    regressed: "no", owner: "Cadastre360 Platform Administration" },
  { id: 26, name: "Executive Security, Identity & Compliance Enhancement", status: "complete", version: "1.0",
    files: ["backend/admin_console_routes.py (extended)", "src/pages/AdminConsole.jsx (extended, responsive)"],
    dbs: ["active_sessions", "evidence_packs"],
    apis: ["/api/cadastre360/admin/sessions", "…/sessions/record", "…/sessions/{id}/terminate",
           "…/sessions/terminate-all/{email}", "…/audit/export.json", "…/audit/evidence-pack",
           "…/audit/evidence-packs", "…/feature-flags"],
    routes: ["/app/admin-console (existing route, 6 tabs)"],
    limitations: "6-tab admin workspace: Identity Administration, Session Management (view + terminate-all-for-user), Audit & Compliance (CSV + JSON), Evidence Packs (SHA-256 signed with QR verify URL + audit number), Identity Federation, Deployment Health. All layouts use responsive tailwind utilities (overflow-x-auto tables, grid stacks on mobile). Existing authentication, JWT and bcrypt unchanged. Session termination records revocation events; live JWT revocation across servers requires a shared blocklist store (production Redis / Mongo TTL index) to be wired.",
    regressed: "no", owner: "Executive Security Programme" },
  { id: 27, name: "Final Enterprise Security & Compliance Hardening", status: "complete", version: "1.0",
    files: ["backend/admin_console_routes.py (extended with PDF + security policy)"],
    dbs: ["security_policy", "session_blocklist"],
    apis: ["/api/cadastre360/admin/audit/evidence-pack/{id}/pdf (signed PDF with masthead + QR + SHA-256)",
           "/api/cadastre360/admin/security/policy (GET/POST)",
           "/api/cadastre360/admin/security/alerts",
           "/api/cadastre360/admin/session/blocklist (GET/POST)",
           "/api/cadastre360/admin/compliance-dashboard"],
    routes: ["/app/admin-console (Evidence tab now downloads real PDFs)"],
    limitations: "Signed PDF evidence via ReportLab 5.0.0 with QR code, sovereign masthead and dual signature block. Configurable security policy (threshold/lockout/auto-unlock/notifications). 9 federation providers registered (Local · Entra ID · Google Workspace · SAML · OIDC · LDAP · Keycloak · Auth0 · Okta — all disabled by default except Local). Feature flags exposed: 10 hardening flags. Redis distributed blocklist is a framework extension — the collection captures revocation intents; live cross-node enforcement binds when the Redis URL env is set at deployment.",
    regressed: "no", owner: "Executive Security Programme" },
];

export const AuditRegister = () => {
  const counts = REGISTER.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  return (
    <div className="space-y-6" data-testid="page-audit-register">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Mandatory Implementation Register</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <ClipboardList className="text-[#C99A2E]"/> Cadastre360™ Capability Audit
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-3xl leading-relaxed">
          Definitive audit of every enhancement listed in the Executive Consolidation Directive. Emergent must consult this register before recommending any capability. No item marked <em>complete</em> may be recreated.
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        {["complete", "partial", "missing"].map((s) => (
          <div key={s} className="cad-panel p-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{s}</div>
            <div className="cad-kpi-value text-3xl mt-1" style={{
              color: s === "complete" ? "#6BB88A" : s === "partial" ? "#C99A2E" : "#E67E7E",
            }}>{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="cad-panel overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left border-b cad-hairline">
              {["#","Capability","Status","Ver","Routes","Files","Limitations / next"].map((h) => (
                <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="cad-tbody">
            {REGISTER.map((r) => (
              <tr key={r.id} data-testid={`audit-${r.id}`}>
                <td className="px-3 py-2 font-mono-cad text-[#C99A2E]">{String(r.id).padStart(2, "0")}</td>
                <td className="px-3 py-2 font-display font-bold text-[#F6F8FA]">{r.name}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {STATUS_ICON[r.status]}
                    <span className={`cad-chip ${r.status === "complete" ? "cad-chip-green" : r.status === "partial" ? "cad-chip-gold" : "cad-chip-red"}`}>{r.status}</span>
                  </span>
                </td>
                <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{r.version}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#7CBFDC]">{r.routes.join(" · ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#B3C2CC]">{r.files.slice(0, 2).join(" · ")}{r.files.length > 2 ? " …" : ""}</td>
                <td className="px-3 py-2 text-[#DCE3E8] leading-relaxed">{r.limitations}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Governance rule</div>
        <div className="text-[13px] text-[#B3C2CC] mt-2 leading-relaxed">
          Emergent will not re-recommend items marked <span className="cad-chip cad-chip-green">complete</span>.
          For items marked <span className="cad-chip cad-chip-gold">partial</span>, only the specific remaining gap
          will be proposed. This register is the source of truth for all future AI code reviews.
        </div>
      </div>
    </div>
  );
};
